import { createAppRequest } from '@/services/request';
import type { ApiRequestOptions } from './types/common';

const request = createAppRequest();

export interface DashboardSummaryRecord {
  materialCount: number;
  stockQty: number;
  stockValue: number;
  warningStockCount: number;
  zeroStockCount: number;
  waitingApprovalCount: number;
  receiveApplyCount: number;
  purchaseApplyCount: number;
  purchaseOrderCount: number;
  todayReceiveApplyCount: number;
  todayIssueCount: number;
  todayPurchaseApplyCount: number;
  todayInboundCount: number;
  waitingIssueCount: number;
  partiallyIssuedCount: number;
  waitingPurchaseCount: number;
  waitingReceiveCount: number;
  ruleRejectedCount: number;
  inventoryHealthIndex: number;
}

export interface InventoryHealthRecord {
  stockUuid: string;
  warehouseUuid: string;
  materialUuid: string;
  currentQty: number;
  frozenQty: number;
  availableQty: number;
  safetyStock: number;
  inventoryValue: number;
  status: string;
  healthScore: number;
  stagnantRisk: boolean;
  safetyGap: number;
}

export interface RankingItemRecord {
  key: string;
  name: string;
  quantity: number;
  amount: number;
  count: number;
}

export interface ReceiveAnalysisRecord {
  totalIssueQty: number;
  totalIssueAmount: number;
  finishedApplyCount: number;
  partiallyIssuedCount: number;
  highRiskApplyCount: number;
  departmentRanking: RankingItemRecord[];
  employeeRanking: RankingItemRecord[];
  materialRanking: RankingItemRecord[];
  abnormalApplies: Array<{
    applyUuid: string;
    applyNo: string;
    applicantMemberUuid: string;
    departmentUuid?: string;
    status: string;
    riskScore: number;
    riskLevel: string;
    estimatedAmount: number;
    issuedAmount: number;
    reason: string;
  }>;
}

export interface PurchaseAnalysisRecord {
  applyCount: number;
  orderCount: number;
  waitingPurchaseCount: number;
  waitingReceiveCount: number;
  receivedOrderCount: number;
  totalPurchaseAmount: number;
  totalArrivalQty: number;
  totalInboundQty: number;
  arrivalCompletionRate: number;
  supplierRanking: RankingItemRecord[];
}

export interface StatisticsInsightRecord {
  code: string;
  level: 'info' | 'warning' | 'critical';
  title: string;
  description: string;
  metricValue: number;
}

export async function getDashboardSummary(options?: ApiRequestOptions) {
  return request<DashboardSummaryRecord>('/bff/statistics/dashboard-summary', {
    method: 'GET',
    ...(options || {}),
  });
}

export async function getInventoryHealth(options?: ApiRequestOptions) {
  return request<InventoryHealthRecord[]>('/bff/statistics/inventory-health', {
    method: 'GET',
    ...(options || {}),
  });
}

export async function getReceiveAnalysis(options?: ApiRequestOptions) {
  return request<ReceiveAnalysisRecord>('/bff/statistics/receive-analysis', {
    method: 'GET',
    ...(options || {}),
  });
}

export async function getPurchaseAnalysis(options?: ApiRequestOptions) {
  return request<PurchaseAnalysisRecord>('/bff/statistics/purchase-analysis', {
    method: 'GET',
    ...(options || {}),
  });
}

export async function getStatisticsInsights(options?: ApiRequestOptions) {
  return request<StatisticsInsightRecord[]>('/bff/statistics/insights', {
    method: 'GET',
    ...(options || {}),
  });
}
